import React, { useState } from 'react';
import styled from 'styled-components';
import { API_ENDPOINTS } from '../../api/config';
import { useAuth } from '../../contexts/AuthContext';
import ElevatedButton from '../Layout/ElevatedButton';

const Form = styled.form`
  max-width: 400px;
  margin: 2rem auto;
  padding: 2rem;
  border: 1px solid ${props => props.theme.buttonPrimary};
  border-radius: 8px;
  background: ${props => props.theme.cardBackground};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  color: ${props => props.theme.text};
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem 1.25rem;
  margin: 0.5rem 0;
  border: 2px solid ${props => props.theme.buttonPrimary};
  border-radius: 4px;
  font-size: 1rem;
  background: ${props => props.theme.inputBackground};
  color: ${props => props.theme.text};
  transition: all 0.3s ease;
  box-sizing: border-box;

  &::placeholder {
    color: ${props => props.theme.textMuted};
  }

  &:focus {
    outline: none;
    border-color: ${props => props.theme.buttonPrimaryHover};
    background: ${props => props.theme.inputBackgroundFocus};
    box-shadow: 0 0 8px rgba(88, 101, 242, 0.3);
  }
`;

const TokenInput = styled(Input)`
  text-align: center;
  font-size: 1.5rem;
  letter-spacing: 0.5rem;

  &::placeholder {
    letter-spacing: normal;
  }
`;

const DangerBox = styled.div`
  padding: 1rem;
  margin: 1rem 0;
  background: linear-gradient(135deg, #f8d7da 0%, #f5c6cb 100%);
  border: 1px solid #dc3545;
  border-radius: 4px;
  color: #721c24;
`;

const DeleteAccountModal = ({ onSuccess, onCancel }) => {
  const [password, setPassword] = useState('');
  const [token, setToken] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Validate password
    if (!password.trim()) {
      setError('Password is required');
      setLoading(false);
      return;
    }

    // Validate 2FA token if enabled
    if (user.twoFactorEnabled) {
      if (token.length !== 6 || !/^\d{6}$/.test(token)) {
        setError('Please enter a valid 6-digit 2FA code');
        setLoading(false);
        return;
      }
    }

    try {
      const response = await fetch(API_ENDPOINTS.DELETE_ACCOUNT, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          password,
          token: user.twoFactorEnabled ? token : undefined
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        onSuccess('Account deleted successfully. You will be logged out.');
      } else {
        setError(data.error || 'Account deletion failed');
      }
    } catch (error) {
      console.error('Account deletion error:', error);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleTokenChange = (e) => {
    const value = e.target.value.replace(/\D/g, ''); // Only allow digits
    if (value.length <= 6) {
      setToken(value);
    }
  };

  // If 2FA is not enabled, only require password
  if (!user.twoFactorEnabled) {
    return (
      <Form onSubmit={handleSubmit}>
        <h2 style={{ color: 'var(--color-text)', textAlign: 'center', textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)' }}>
          Delete Account
        </h2>

        <DangerBox>
          <strong>Warning: This action cannot be undone!</strong>
          <br />
          Deleting your account will permanently remove all your data including courses, assignments, and settings.
        </DangerBox>

        <p style={{ textAlign: 'center', color: '#b9bbbe', marginBottom: '1rem' }}>
          Enter your password to confirm account deletion
        </p>

        {error && (
          <div style={{
            padding: '0.75rem',
            marginBottom: '1rem',
            background: '#dc3545',
            color: 'white',
            borderRadius: '4px',
            textAlign: 'center'
          }}>
            {error}
          </div>
        )}

        <Input
          type="password"
          placeholder="Current Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          disabled={loading}
          autoFocus
        />

        <ElevatedButton
          type="submit"
          style={{
            width: '100%',
            marginTop: '1rem',
            background: 'linear-gradient(135deg, #dc3545 0%, #c82333 100%)',
            border: '2px solid #dc3545'
          }}
          disabled={loading}
        >
          {loading ? 'Deleting...' : 'Permanently Delete Account'}
        </ElevatedButton>

        <ElevatedButton
          type="button"
          onClick={onCancel}
          style={{
            width: '100%',
            marginTop: '0.5rem',
            background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%)',
            border: '2px solid #ff4757'
          }}
        >
          Cancel
        </ElevatedButton>
      </Form>
    );
  }

  return (
    <Form onSubmit={handleSubmit}>
      <h2 style={{ color: 'var(--color-text)', textAlign: 'center', textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)' }}>
        Delete Account
      </h2>

      <DangerBox>
        <strong>Warning: This action cannot be undone!</strong>
        <br />
        Deleting your account will permanently remove all your data including courses, assignments, and settings.
      </DangerBox>

      <p style={{ textAlign: 'center', color: '#b9bbbe', marginBottom: '1rem' }}>
        Enter your password and 2FA code to confirm account deletion
      </p>

      {error && (
        <div style={{
          padding: '0.75rem',
          marginBottom: '1rem',
          background: '#dc3545',
          color: 'white',
          borderRadius: '4px',
          textAlign: 'center'
        }}>
          {error}
        </div>
      )}

      <Input
        type="password"
        placeholder="Current Password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        required
        disabled={loading}
        autoFocus
      />

      <TokenInput
        type="text"
        placeholder="000000"
        value={token}
        onChange={handleTokenChange}
        required
        disabled={loading}
      />

      <ElevatedButton
        type="submit"
        style={{
          width: '100%',
          marginTop: '1rem',
          background: 'linear-gradient(135deg, #dc3545 0%, #c82333 100%)',
          border: '2px solid #dc3545'
        }}
        disabled={loading || token.length !== 6}
      >
        {loading ? 'Deleting...' : 'Permanently Delete Account'}
      </ElevatedButton>

      <ElevatedButton
        type="button"
        onClick={onCancel}
        style={{
          width: '100%',
          marginTop: '0.5rem',
          background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%)',
          border: '2px solid #ff4757'
        }}
      >
        Cancel
      </ElevatedButton>
    </Form>
  );
};

export default DeleteAccountModal;